import { Status } from '@essent/common';
import type { FlowId } from '@essent/extension-offer';
import {
  putProposition,
  putPropositionClear,
  putPropositionError,
  putPropositionSuccess,
} from '@essent/extension-offer';
import type { GetRetentionLegacyOfferResponse } from '@innogy/retention/services';
import {
  mapDurationsFromOffersWithEstimates,
  mapOfferWithEstimatesToLegacyOffer,
} from '@innogy/retention/services';
import type { CollectionState } from '@innogy/utils-state';
import {
  createInitialCollectionState,
  updateCollectionState,
} from '@innogy/utils-state';
import type { Action } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';
import { marketingWrapperActions } from '@innogy/marketing/store';

import {
  chooseRetentionProposition,
  setSelectedOfferAction,
} from './retention-offer.actions';

export type GetOfferState = CollectionState<GetRetentionLegacyOfferResponse>;
export type UpdateOfferState = CollectionState<FlowId>;

export interface OfferState {
  getOfferState: GetOfferState;
  updateOfferState: UpdateOfferState;
  selectedCampaignId?: string;
  selectedDuration?: string;
  orderPage?: string;
}

export const initialState: OfferState = {
  getOfferState: createInitialCollectionState(),
  updateOfferState: createInitialCollectionState(),
  orderPage: undefined,
  selectedCampaignId: undefined,
  selectedDuration: undefined,
};

const _getOfferReducer = createReducer(
  initialState,
  on(
    marketingWrapperActions.getNextBestOfferWithEstimatesV4.requestAction,
    (state, action) => ({
      ...state,
      getOfferState: updateCollectionState(state.getOfferState, action, {
        status: Status.PENDING,
      }),
    })
  ),
  on(
    marketingWrapperActions.getNextBestOfferWithEstimatesV4.successAction,
    (state, action) => {
      const mappedOffers = (action.payload.offers ?? [])
        .filter((offer) => offer.content)
        .map(mapOfferWithEstimatesToLegacyOffer);

      const mappedDurations = (action.payload.offers ?? [])
        .filter((offer) => offer.content)
        .map(mapDurationsFromOffersWithEstimates);

      const mappedPayload: GetRetentionLegacyOfferResponse = {
        offers: mappedOffers,
        durations: mappedDurations,
        isCustomer: true,
      };

      return {
        ...state,
        getOfferState: updateCollectionState(state.getOfferState, action, {
          status: Status.SUCCESS,
          entry: mappedPayload,
        }),
      };
    }
  ),
  on(
    marketingWrapperActions.getNextBestOfferWithEstimatesV4.errorAction,
    (state, action) => ({
      ...state,
      getOfferState: updateCollectionState(state.getOfferState, action, {
        status: Status.ERROR,
        error: action.payload,
      }),
    })
  ),
  on(putProposition, (state, action) => ({
    ...state,
    updateOfferState: updateCollectionState(state.updateOfferState, action, {
      status: Status.PENDING,
    }),
  })),
  on(putPropositionSuccess, (state, action) => ({
    ...state,
    updateOfferState: updateCollectionState(state.updateOfferState, action, {
      status: Status.SUCCESS,
      entry: action.payload,
    }),
  })),
  on(putPropositionError, (state, action) => ({
    ...state,
    updateOfferState: updateCollectionState(state.updateOfferState, action, {
      status: Status.ERROR,
      error: action.payload,
    }),
  })),
  on(setSelectedOfferAction, (state, action) => ({
    ...state,
    selectedCampaignId: action.payload.campaignId,
    selectedDuration: action.payload.duration,
  })),
  on(chooseRetentionProposition, (state, action) => ({
    ...state,
    orderPage: action.payload.orderPage,
  })),
  on(putPropositionClear, (state) => ({
    ...state,
    updateOfferState: initialState.updateOfferState,
  })),
  on(
    marketingWrapperActions.getNextBestOfferWithEstimatesV4.clearAction,
    (_) => initialState
  )
);

export function getOfferReducer(
  state: OfferState = initialState,
  action: Action
): OfferState {
  return _getOfferReducer(state, action);
}
